import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchVehicleServiceIntervals,
  createVehicleServiceInterval,
  updateVehicleServiceInterval,
  fetchVehicleServices,
  createVehicleService,
  updateVehicleService,
} from "../../../../api/vehicles/service";
import { DocumentManagerV6 } from "../../Media/DocumentManagerV6";
import { NotesAdvancedSection } from "../../notes/notesAdvancedSection";
import { fetchSupplierInvoices } from "../../../../api/supplierinvoices";
import { PMDialog } from "../../../utils/PMDialog";
import { PencilIcon, ChevronDownIcon, ChevronUpIcon, LinkIcon, DocumentTextIcon } from "@heroicons/react/24/outline";

export const VehicleServicePanel = ({ vehicleId, canManage = true }) => {
  const { t } = useTranslation();
  const [intervals, setIntervals] = useState([]);
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showIntervalDialog, setShowIntervalDialog] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [showServiceDetailsDialog, setShowServiceDetailsDialog] = useState(false);
  const [expandedIntervals, setExpandedIntervals] = useState({});
  const [selectedService, setSelectedService] = useState(null);
  const [editingInterval, setEditingInterval] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [selectedIntervalForService, setSelectedIntervalForService] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadData = async () => {
    if (!vehicleId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [ints, servs] = await Promise.all([
        fetchVehicleServiceIntervals({ filters: [{ name: "vehicle_id", value: vehicleId }], per_page: 50 }),
        fetchVehicleServices({ filters: [{ name: "vehicle_id", value: vehicleId }], per_page: 50, sortBy: "performed_at", sort: "DESC" }),
      ]);
      setIntervals(ints?.data || []);
      setServices(servs?.data || []);
    } catch (e) {
      setError("Failed to load vehicle service data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [vehicleId]);

  // Load supplier invoices when service dialog opens
  useEffect(() => {
    async function loadSupplierInvoices() {
      if (showServiceDialog) {
        try {
          const response = await fetchSupplierInvoices({ per_page: 100, sortBy: "invoice_date", sort: "DESC" });
          setSupplierInvoices(response?.data || []);
        } catch (e) {
          console.error("Failed to load supplier invoices:", e);
        }
      }
    }
    loadSupplierInvoices();
  }, [showServiceDialog]);

  const handleCreateInterval = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const form = e.target;
    const payload = {
      vehicle_id: vehicleId,
      title: form.title.value,
      interval_kilometers: form.interval_kilometers.value || null,
      interval_months: form.interval_months.value || null,
      notes: form.notes.value,
    };
    try {
      const res = editingInterval
        ? await updateVehicleServiceInterval(editingInterval.id, payload)
        : await createVehicleServiceInterval(payload);
      if (res?.id) {
        setShowIntervalDialog(false);
        setEditingInterval(null);
        await loadData();
      }
    } catch (error) {
      console.error("Failed to save interval:", error);
      setError("Failed to save interval");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateService = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const form = e.target;
    const payload = {
      vehicle_id: vehicleId,
      vehicle_service_interval_id: form.vehicle_service_interval_id.value || null,
      supplier_invoice_id: form.supplier_invoice_id.value || null,
      performed_at: form.performed_at.value,
      odometer_kilometers: form.odometer_kilometers.value || null,
      description: form.description.value,
      cost_nok: form.cost_nok.value || null,
    };
    try {
      const res = editingService
        ? await updateVehicleService(editingService.id, payload)
        : await createVehicleService(payload);
      if (res?.id) {
        setShowServiceDialog(false);
        setEditingService(null);
        setSelectedIntervalForService(null);
        await loadData();
      }
    } catch (error) {
      console.error("Failed to save service:", error);
      setError("Failed to save service");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditInterval = (interval) => {
    setEditingInterval(interval);
    setShowIntervalDialog(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setSelectedIntervalForService(service.vehicle_service_interval_id);
    setShowServiceDialog(true);
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setShowServiceDetailsDialog(true);
  };

  const toggleIntervalExpanded = (intervalId) => {
    setExpandedIntervals((prev) => ({
      ...prev,
      [intervalId]: !prev[intervalId],
    }));
  };

  const getServicesForInterval = (intervalId) => {
    return services.filter((s) => s.vehicle_service_interval_id === intervalId);
  };

  return (
    <div className="space-y-6">
      {/* Service Intervals Section */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t("Service Intervals")}</h3>
          <p className="text-sm text-gray-500">{t("Define recurring service intervals")}</p>
        </div>
        {canManage && (
          <button
            onClick={() => {
              setEditingInterval(null);
              setShowIntervalDialog(true);
            }}
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
          >
            {t("Add Interval")}
          </button>
        )}
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {isLoading ? (
            <div className="text-sm text-gray-500">{t("Loading...")}</div>
          ) : error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : intervals.length === 0 ? (
            <div className="text-sm text-gray-500">{t("No intervals defined yet")}</div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {intervals.map((i) => {
                const intervalServices = getServicesForInterval(i.id);
                const isExpanded = expandedIntervals[i.id];
                return (
                  <li key={i.id} className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-gray-900">{i.title}</div>
                          {canManage && (
                            <button
                              onClick={() => handleEditInterval(i)}
                              className="text-gray-400 hover:text-blue-600"
                              title={t("Edit interval")}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {i.interval_kilometers ? `${i.interval_kilometers} km` : "-"} · {i.interval_months ? `${i.interval_months} mnd` : "-"}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {i.last_service_at ? (
                            <>
                              {t("Last service")}: {new Date(i.last_service_at).toLocaleDateString()}
                              {i.next_service_at && (
                                <> · {t("Next service")}: {new Date(i.next_service_at).toLocaleDateString()}</>
                              )}
                            </>
                          ) : (
                            t("No services performed yet")
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {intervalServices.length > 0 && (
                          <span className="text-xs text-gray-500">
                            {intervalServices.length} {t("service(s)")}
                          </span>
                        )}
                        <button
                          onClick={() => toggleIntervalExpanded(i.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {isExpanded ? (
                            <ChevronUpIcon className="h-5 w-5" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Expanded interval services */}
                    {isExpanded && (
                      <div className="mt-4 pl-4 border-l-2 border-gray-200 space-y-3">
                        {intervalServices.length > 0 ? (
                          intervalServices.map((service) => (
                            <div
                              key={service.id}
                              className="p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 cursor-pointer" onClick={() => handleServiceClick(service)}>
                                  <div className="font-medium text-sm text-gray-900">
                                    {service.performed_at ? new Date(service.performed_at).toLocaleDateString() : ""}
                                  </div>
                                  {service.odometer_kilometers && (
                                    <div className="text-xs text-gray-500">
                                      {service.odometer_kilometers} km
                                    </div>
                                  )}
                                  {service.description && (
                                    <div className="text-sm text-gray-700 mt-1">{service.description}</div>
                                  )}
                                  {service.supplier_invoice && (
                                    <div className="flex items-center gap-1 mt-1 text-xs text-blue-600">
                                      <LinkIcon className="h-3 w-3" />
                                      {service.supplier_invoice.invoice_number}
                                    </div>
                                  )}
                                </div>
                                {canManage && (
                                  <button
                                    onClick={() => handleEditService(service)}
                                    className="text-gray-400 hover:text-blue-600 ml-2"
                                    title={t("Edit service")}
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-sm text-gray-500 py-2">
                            {t("No services performed for this interval")}
                          </div>
                        )}
                        {canManage && (
                          <button
                            onClick={() => {
                              setSelectedIntervalForService(i.id);
                              setEditingService(null);
                              setShowServiceDialog(true);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            + {t("Add service to this interval")}
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Service History Section */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t("Service History")}</h3>
          <p className="text-sm text-gray-500">{t("What was done and when")}</p>
        </div>
        {canManage && (
          <button
            onClick={() => {
              setSelectedIntervalForService(null);
              setEditingService(null);
              setShowServiceDialog(true);
            }}
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
          >
            {t("Add Service")}
          </button>
        )}
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          {isLoading ? (
            <div className="text-sm text-gray-500">{t("Loading...")}</div>
          ) : error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : services.length === 0 ? (
            <div className="text-sm text-gray-500">{t("No services yet")}</div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {services.map((s) => (
                <li
                  key={s.id}
                  className="py-3 cursor-pointer transition-colors hover:bg-gray-50"
                  onClick={() => handleServiceClick(s)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {s.performed_at ? new Date(s.performed_at).toLocaleDateString() : ""}
                      </div>
                      {s.odometer_kilometers && (
                        <div className="text-sm text-gray-500">{s.odometer_kilometers} km</div>
                      )}
                      {s.description && (
                        <div className="text-sm text-gray-700 mt-1">{s.description}</div>
                      )}
                      {s.supplier_invoice && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-blue-600">
                          <LinkIcon className="h-3 w-3" />
                          {t("Invoice")}: {s.supplier_invoice.invoice_number}
                        </div>
                      )}
                      {s.cost_nok && (
                        <div className="text-sm text-gray-600 mt-1">
                          {t("Cost")}: {s.cost_nok.toLocaleString("no-NO")} kr
                        </div>
                      )}
                    </div>
                    {canManage && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditService(s);
                        }}
                        className="text-gray-400 hover:text-blue-600 ml-2"
                        title={t("Edit service")}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Service Details Dialog */}
      <PMDialog
        open={showServiceDetailsDialog}
        onClose={() => {
          setShowServiceDetailsDialog(false);
          setSelectedService(null);
        }}
        title={selectedService ? `${t("Service Details")} - ${selectedService.performed_at ? new Date(selectedService.performed_at).toLocaleDateString() : ""}` : t("Service Details")}
        size="4xl"
      >
        {selectedService && (
          <div className="space-y-6">
            {/* Documents */}
            <div>
              <h5 className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                <DocumentTextIcon className="h-4 w-4" />
                {t("Documents")}
              </h5>
              <DocumentManagerV6
                module="vehicle_service"
                moduleParam={selectedService.id}
                mode="dropzone"
                showUpload={canManage}
                allowDelete={canManage}
                allowEdit={canManage}
                autoLoad={true}
              />
            </div>

            {/* Notes */}
            <div>
              <h5 className="text-sm font-medium text-gray-900 mb-2">{t("Notes")}</h5>
              <NotesAdvancedSection
                module="vehicle_service"
                module_param={selectedService.id}
                title={t("Service Notes")}
                enableFileUpload={false}
                enableTemplates={canManage}
                enableTags={canManage}
                disabled={!canManage}
              />
            </div>
          </div>
        )}
      </PMDialog>

      {/* Create/Edit Interval Dialog */}
      <PMDialog
        open={showIntervalDialog}
        onClose={() => {
          setShowIntervalDialog(false);
          setEditingInterval(null);
        }}
        title={editingInterval ? t("Edit Interval") : t("Create Interval")}
        size="md"
      >
        <form onSubmit={handleCreateInterval}>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("Title")}</label>
              <input
                name="title"
                type="text"
                className="w-full border rounded px-3 py-2"
                placeholder={t("Title")}
                required
                defaultValue={editingInterval?.title || ""}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Kilometers")}</label>
                <input
                  name="interval_kilometers"
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  placeholder={t("Kilometers") + " (e.g. 15000)"}
                  defaultValue={editingInterval?.interval_kilometers || ""}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("Months")}</label>
                <input
                  name="interval_months"
                  type="number"
                  className="w-full border rounded px-3 py-2"
                  placeholder={t("Months") + " (e.g. 12)"}
                  defaultValue={editingInterval?.interval_months || ""}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("Notes")} ({t("Optional")})</label>
              <textarea
                name="notes"
                className="w-full border rounded px-3 py-2"
                placeholder={t("Notes (optional)")}
                rows="3"
                defaultValue={editingInterval?.notes || ""}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowIntervalDialog(false);
                setEditingInterval(null);
              }}
              disabled={isSaving}
              className="px-3 py-2 rounded border disabled:opacity-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? t("Saving...") : editingInterval ? t("Update") : t("Create")}
            </button>
          </div>
        </form>
      </PMDialog>

      {/* Create/Edit Service Dialog */}
      <PMDialog
        open={showServiceDialog}
        onClose={() => {
          setShowServiceDialog(false);
          setEditingService(null);
          setSelectedIntervalForService(null);
        }}
        title={editingService ? t("Edit Service") : t("Add Service")}
        size="md"
      >
        <form onSubmit={handleCreateService}>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("Service Interval")} ({t("Optional")})
              </label>
              <select
                name="vehicle_service_interval_id"
                className="w-full border rounded px-3 py-2"
                defaultValue={editingService?.vehicle_service_interval_id || selectedIntervalForService || ""}
              >
                <option value="">{t("No interval")}</option>
                {intervals.map((interval) => (
                  <option key={interval.id} value={interval.id}>
                    {interval.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("Date")}</label>
              <input
                name="performed_at"
                type="date"
                className="w-full border rounded px-3 py-2"
                required
                defaultValue={editingService?.performed_at ? editingService.performed_at.split("T")[0] : ""}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("Odometer (km)")}</label>
              <input
                name="odometer_kilometers"
                type="number"
                className="w-full border rounded px-3 py-2"
                placeholder={t("Odometer (km)")}
                defaultValue={editingService?.odometer_kilometers || ""}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("Description")}</label>
              <textarea
                name="description"
                className="w-full border rounded px-3 py-2"
                placeholder={t("Description")}
                rows="3"
                defaultValue={editingService?.description || ""}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("Cost (NOK)")}</label>
              <input
                name="cost_nok"
                type="number"
                step="0.01"
                className="w-full border rounded px-3 py-2"
                placeholder={t("Cost (NOK)")}
                defaultValue={editingService?.cost_nok || ""}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("Supplier Invoice")} ({t("Optional")})
              </label>
              <select
                name="supplier_invoice_id"
                className="w-full border rounded px-3 py-2"
                defaultValue={editingService?.supplier_invoice_id || ""}
              >
                <option value="">{t("No invoice")}</option>
                {supplierInvoices.map((invoice) => (
                  <option key={invoice.id} value={invoice.id}>
                    {invoice.invoice_number} - {invoice.supplier?.name || t("Unknown supplier")}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowServiceDialog(false);
                setEditingService(null);
                setSelectedIntervalForService(null);
              }}
              disabled={isSaving}
              className="px-3 py-2 rounded border disabled:opacity-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? t("Saving...") : editingService ? t("Update") : t("Save")}
            </button>
          </div>
        </form>
      </PMDialog>
    </div>
  );
};

export default VehicleServicePanel;
